import moment from "moment";
import UtilityService from "./UtilityService";
import PlaybookWaitingService from "./PlaybookWaitingService";
import PlaybookBooksService from "./PlaybookBooksService";
import { postToAgents } from "./PlaybookApi";
import { SEVERITY_GROUPS } from "../constants/PlaybookReview";

const GL_ALLOCATION_CHECK_ID = "gl_allocation";
const PROPOSALS_SECTION_TITLE = "Proposed journal entries";
const STAGE_DESCRIPTIONS = {
	needs_decisions: { label: "Needs decisions", tone: "warning" },
	waiting_on_signoff: { label: "Waiting on sign-off", tone: "accent" },
	signed_off: { label: "Signed off", tone: "success" },
	complete: { label: "Decided", tone: "success" },
};
const SIGNOFF_STAGE_LABELS = {
	sent_for_review: "Sent for review",
	signed_off: "Signed off",
	returned: "Returned to the preparer",
};
const REJECT_REASON_LABELS = {
	not_an_error: "Not an error",
	client_explained: "Client has explained",
	timing: "Timing — will be corrected later",
	below_materiality: "Too small to matter for this client",
	job_or_project: "Belongs to a job or project",
	vendor_rule: "The vendor is always coded this way",
	prior_year_treatment: "Treated this way in prior years",
	client_policy: "The client's own policy",
	tax_treatment: "Tax treatment",
	misread_description: "The agent misread the description",
	missing_information: "Missing information — ask the client",
	other: "Other",
};
const SCRIPT_FROM_LIBRARY = "library";
const APPROVER_NO_LONGER_LISTED = "someone no longer listed";
const REASON_NEEDING_A_NOTE = "other";
const REASON_WITHOUT_AN_ANSWER = "missing_information";
const WORKFLOW_ACTIONS_SECTION_TITLE = "Proposed Workflow actions";
const FINISHED_JOURNAL_STATES = ["booked", "removed", "rejected"];
const PROPOSAL_KIND = {
	journal: "journal",
	workflow: "workflow",
};
const PROOF_KIND = {
	tracked: "tracked",
	cited: "cited",
	check: "check",
	none: "none",
};
const SOURCE_REPORT_WORDS = {
	GeneralLedger: "the general ledger",
	TrialBalance: "the trial balance",
	ProfitAndLoss: "the profit and loss",
	BalanceSheet: "the balance sheet",
	JournalEntry: "the journal entries",
};
const SOURCE_WITHOUT_A_NAME = "the source";
const FROZEN_ROW_TEXT_FIELDS = ["type", "number", "name", "account"];
const FROZEN_ROW_DETAIL_FIELDS = ["memo", "description", "split"];
const FROZEN_ROW_DATE_FORMAT = "MMM D, YYYY";
const ROW_PART_SEPARATOR = " · ";
const UNPROVEN_FACT_REFUSAL_WORDS = "is not in what QuickBooks returned";
const KEY_WORD_SEPARATOR = "_";
const TOOL_KIND_SEPARATOR = ":";
const ISO_DATE_LENGTH = 10;
const ISO_DAY_FORMAT = "YYYY-MM-DD";
const FACT_TYPE = {
	amount: "amount",
	date: "date",
	account: "account",
};

const PlaybookReviewService = {
	/**
	 * One run's suggestions with their decisions, last month's decisions on the same problems,
	 * the latest re-check of the books, the sign-off history, who may act and what the viewer may
	 * do. Every review action below resolves to this same shape, freshly read.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @return {Promise<Object>}
	 */
	getReview({ runId }) {
		return postToAgents({ operationId: "getPlaybookRunReview", body: { runId } });
	},

	/**
	 * One finding's proof and the rows the agent read for it, frozen as the run stored them, for the
	 * "What the agent read" tab. Each step lists its cited rows first, marked isCited.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @param {Number} params.findingId
	 * @return {Promise<Object>} { proof: { proofKind, facts, citedRows, report }, steps: [{ sequence, toolName, label, provenance, isTruncated, truncatedNote, rows: [{ rowId, values, isCited }] }] }
	 */
	getFindingEvidence({ runId, findingId }) {
		return postToAgents({ operationId: "getPlaybookFindingEvidence", body: { runId, findingId } });
	},

	/**
	 * A finding's proof line: its tone and one summary, worked out from the proof the server sends
	 * with every finding. Any figure that failed its check makes the line failing ("1 figure not in
	 * the source"), whatever the others did. When every checked figure was found it is verified and
	 * says where and for which days ("3 of 3 figures found in the general ledger, 1 Sep – 30 Sep").
	 * When some figures could not be checked at all and none failed, the count is shown in a neutral
	 * tone, so a partly checked finding never looks proven. Figures with no status were worked out
	 * by code ("Calculated by code"), and a finding that cites nothing says "No figures cited".
	 * The figures counted are the rows describeProofFacts shows for the same proof and card account,
	 * with their tones, so the line never counts a figure the row does not show or merged away.
	 *
	 * @param {Object} params
	 * @param {Object|null} params.proof { proofKind, facts: [{ status }], report: { name, startDate, endDate } | null }
	 * @param {Object|null} [params.cardAccount] the finding's own account, { qboAccountId, name }, as describeProofFacts takes it
	 * @return {Object} { tone: "verified" | "failing" | "code" | "none", summary }
	 */
	describeProof({ proof, cardAccount = null }) {
		const proofKind = proof ? proof.proofKind : PROOF_KIND.none;
		const facts = proof && Array.isArray(proof.facts) ? proof.facts : [];
		const isCodeOnly = proofKind === PROOF_KIND.check && facts.length === 0;
		if (isCodeOnly) return { tone: "code", summary: "Calculated by code" };
		const citesNothing = proofKind === PROOF_KIND.none || facts.length === 0;
		if (citesNothing) return { tone: "none", summary: "No figures cited" };

		const shownFacts = this.describeProofFacts({ proof, cardAccount });
		const factTones = shownFacts.map(fact => fact.tone);
		const checkedTones = factTones.filter(tone => tone !== "code");
		if (checkedTones.length === 0) return { tone: "code", summary: "Calculated by code" };

		const failingCount = checkedTones.filter(tone => tone === "failing").length;
		if (failingCount > 0) {
			const failingNoun = failingCount === 1 ? "figure" : "figures";
			const failingSummary = `${failingCount} ${failingNoun} not in the source`;
			return { tone: "failing", summary: failingSummary };
		}

		const foundCount = checkedTones.filter(tone => tone === "verified").length;
		const checkedCount = checkedTones.length;
		const checkedNoun = checkedCount === 1 ? "figure" : "figures";
		const report = proof.report || null;
		const reportName = report ? report.name : null;
		const isKnownReport = !!reportName && Object.prototype.hasOwnProperty.call(SOURCE_REPORT_WORDS, reportName);
		const sourceWords = isKnownReport ? SOURCE_REPORT_WORDS[reportName] : SOURCE_WITHOUT_A_NAME;
		const reportAsAStep = { provenance: report ? { startDate: report.startDate, endDate: report.endDate } : null, rows: [] };
		const { periodText } = this.describeFrozenStep({ step: reportAsAStep });
		const periodWords = periodText ? `, ${periodText}` : "";
		const isEveryFigureFound = foundCount === checkedCount;
		const tone = isEveryFigureFound ? "verified" : "none";
		const summary = `${foundCount} of ${checkedCount} ${checkedNoun} found in ${sourceWords}${periodWords}`;
		return { tone, summary };
	},

	/**
	 * Each stated figure of a finding's proof, for the proof row: its tone and check words (through
	 * PlaybookBooksService.describeFactStatus, the same words the proof line counts), its label and
	 * its value as a person reads it. Its label is the one the server sends, or, when the server
	 * sends none, its key as words ("payer_name" reads "Payer name"). How the value reads follows the
	 * type the server declares for the figure, never its key: an amount holding a number is shown as
	 * money; a date is read as the calendar day in its first ten characters (YYYY-MM-DD, the same
	 * characters the server checks it by) and shown the way the rest of the page writes dates ("Sep
	 * 26, 2026"), with no time-zone shift, so a timestamp at midnight UTC never reads as the day
	 * before; a date that is not that shape keeps its value as recorded; an account is shown by the name a cited row gives that account id, and an account
	 * holding the card's own QuickBooks account id is shown by the card's account name, with no value
	 * when no name is known, never as the bare id. A figure with any other type, or with no type (an
	 * older record), keeps its value as recorded. Figures holding the card's account are shown once
	 * only when they carry the same status, in the place of the first; one with a different status
	 * keeps its own row and its own label, so one figure's status is never shown under another's
	 * label. A figure calculated by code carries no label of its own, because the proof line already
	 * says "Calculated by code" once; only its value is shown, and a figure left with neither label
	 * nor value is dropped. describeProof counts these same rows.
	 *
	 * @param {Object} params
	 * @param {Object|null} params.proof { facts: [{ field, type, label, value, status }], citedRows: [{ values: { accountId, account } }] }
	 * @param {Object|null} [params.cardAccount] the finding's own account, { qboAccountId, name }
	 * @return {Array} [{ key, tone: "verified" | "failing" | "code" | "none", label, statusText, value }]
	 */
	describeProofFacts({ proof, cardAccount = null }) {
		const facts = proof && Array.isArray(proof.facts) ? proof.facts : [];
		const citedRows = proof && Array.isArray(proof.citedRows) ? proof.citedRows : [];
		const cardAccountId = cardAccount && cardAccount.qboAccountId ? String(cardAccount.qboAccountId) : null;
		const cardAccountName = cardAccount && cardAccount.name ? cardAccount.name : null;
		const describedFacts = facts.map((fact, index) => {
			const status = PlaybookBooksService.describeFactStatus({ status: fact.status });
			const fieldKey = String(fact.field || "");
			const fieldKeyWords = fieldKey.split(KEY_WORD_SEPARATOR).filter(Boolean);
			const fieldSentence = fieldKeyWords.join(" ");
			const fieldReadableWords = fieldSentence.charAt(0).toUpperCase() + fieldSentence.slice(1);
			const isAmountFact = fact.type === FACT_TYPE.amount;
			const isDateFact = fact.type === FACT_TYPE.date;
			const isAccountFact = fact.type === FACT_TYPE.account;
			const isCalculatedByCode = status.tone === "code";
			const hasValue = fact.value !== null && fact.value !== undefined;
			const recordedValue = hasValue ? String(fact.value) : "";
			const numericValue = Number(recordedValue);
			const holdsANumber = recordedValue !== "" && Number.isFinite(numericValue);
			const recordedCalendarDay = recordedValue.slice(0, ISO_DATE_LENGTH);
			const parsedCalendarDay = moment(recordedCalendarDay, ISO_DAY_FORMAT, true);
			const holdsAnIsoDate = parsedCalendarDay.isValid();
			const rowNamingTheAccount = isAccountFact && hasValue ? citedRows.find(row => !!row.values && !!row.values.account && String(row.values.accountId) === recordedValue) : null;
			const isTheCardAccount = isAccountFact && hasValue && !!cardAccountId && recordedValue === cardAccountId;
			const rowAccountName = rowNamingTheAccount ? rowNamingTheAccount.values.account : null;
			let value = recordedValue;
			if (isAmountFact && holdsANumber) value = UtilityService.formatSignedCurrency({ value: numericValue });
			if (isDateFact && holdsAnIsoDate) value = parsedCalendarDay.format(FROZEN_ROW_DATE_FORMAT);
			if (rowAccountName) value = rowAccountName;
			if (isTheCardAccount) value = rowAccountName || cardAccountName || "";
			const readableLabel = fact.label || fieldReadableWords;
			const label = isCalculatedByCode ? null : readableLabel;
			const describedFact = { key: `${fieldKey}-${index}`, tone: status.tone, label, statusText: status.label, value, isTheCardAccount };
			return describedFact;
		});

		const cardAccountFacts = describedFacts.filter(fact => fact.isTheCardAccount);
		const factsOncePerAccountAndStatus = describedFacts.filter(fact => {
			if (!fact.isTheCardAccount) return true;
			const firstCardFactWithThisTone = cardAccountFacts.find(cardFact => cardFact.tone === fact.tone);
			const isFirstWithThisTone = firstCardFactWithThisTone === fact;
			return isFirstWithThisTone;
		});
		const factsForTheRow = factsOncePerAccountAndStatus.map(fact => {
			const shownFact = { key: fact.key, tone: fact.tone, label: fact.label, statusText: fact.statusText, value: fact.value };
			return shownFact;
		});
		const shownFacts = factsForTheRow.filter(fact => !!fact.label || !!fact.value);
		return shownFacts;
	},

	/**
	 * One step of what the agent read, frozen as the run stored it, for the panel's first tab: its
	 * title (the tool's own label from the server, or, when the server sends none, the tool's name as
	 * words with any kind prefix before a colon left off), the days it read ("1 Sep – 30 Sep", with years
	 * when the days fall in two years), the server's note when QuickBooks returned more rows than
	 * were kept, and its rows with the cited ones first. A row reads as its type, number, name and
	 * account; its detail is its memo, description and split; its amount is money when it has one.
	 * A row with none of those words reads as its own row id. The days come from the step's
	 * provenance, as a period { start, end } or as startDate and endDate; describeProof words a
	 * report's days through this same function, so both say days the same way.
	 *
	 * @param {Object} params
	 * @param {Object} params.step { sequence, toolName, label, provenance, truncatedNote, rows: [{ rowId, values, isCited }] }
	 * @return {Object} { key, title, periodText, truncatedNote, rows: [{ key, isCited, date, text, detail, amount }] }
	 */
	describeFrozenStep({ step }) {
		const provenance = step.provenance || {};
		const period = provenance.period || null;
		const startDate = period ? period.start : provenance.startDate;
		const endDate = period ? period.end : provenance.endDate;
		const hasPeriod = !!startDate && !!endDate;
		let periodText = null;
		if (hasPeriod) {
			const isOneYear = moment(startDate).year() === moment(endDate).year();
			const dayFormat = isOneYear ? "D MMM" : "D MMM YYYY";
			const firstDay = moment(startDate).format(dayFormat);
			const lastDay = moment(endDate).format(dayFormat);
			periodText = `${firstDay} – ${lastDay}`;
		}

		const stepRows = Array.isArray(step.rows) ? step.rows : [];
		const citedRows = stepRows.filter(row => row.isCited);
		const otherRows = stepRows.filter(row => !row.isCited);
		const orderedRows = [...citedRows, ...otherRows];
		const rows = orderedRows.map((row, index) => {
			const values = row.values || {};
			const textParts = FROZEN_ROW_TEXT_FIELDS.map(field => values[field]).filter(Boolean);
			const detailParts = FROZEN_ROW_DETAIL_FIELDS.map(field => values[field]).filter(Boolean);
			const hasAmount = values.amount !== null && values.amount !== undefined && values.amount !== "";
			const hasText = textParts.length > 0;
			const text = hasText ? textParts.join(ROW_PART_SEPARATOR) : String(row.rowId);
			const detail = detailParts.length > 0 ? detailParts.join(ROW_PART_SEPARATOR) : null;
			const date = values.date ? moment(values.date).format(FROZEN_ROW_DATE_FORMAT) : null;
			const amount = hasAmount ? UtilityService.formatSignedCurrency({ value: values.amount }) : null;
			const describedRow = { key: `${row.rowId}-${index}`, isCited: !!row.isCited, date, text, detail, amount };
			return describedRow;
		});

		const toolNameParts = String(step.toolName || "").split(TOOL_KIND_SEPARATOR);
		const bareToolName = toolNameParts[toolNameParts.length - 1];
		const toolNameWords = bareToolName.split(KEY_WORD_SEPARATOR).filter(Boolean);
		const toolNameSentence = toolNameWords.join(" ");
		const toolNameReadable = toolNameSentence.charAt(0).toUpperCase() + toolNameSentence.slice(1);
		const title = step.label || toolNameReadable || null;
		const frozenStep = { key: `step-${step.sequence}`, title, periodText, truncatedNote: step.truncatedNote || null, rows };
		return frozenStep;
	},

	/**
	 * The sentence shown in place of the book button when a journal proposal cannot be booked
	 * because a figure of a finding it answers is unproven, or null when nothing of that kind blocks
	 * it. The review's blockedByFacts ([{ refusal }]) is used when the server sends any; until it
	 * does, the same sentences are picked from the booking preview's refusals, which the server
	 * words as "<figure> on <account> is not in what QuickBooks returned, so this entry can't be
	 * booked. Re-run the playbook for fresh figures." The preview's other refusals (already booked,
	 * who may book, QuickBooks no longer taking the entry) are not about facts and stay in the
	 * booking modal. One blocking figure gives its own sentence; several are counted in one.
	 *
	 * @param {Object} params
	 * @param {Object} params.proposal a journal proposal from the review, with blockedByFacts
	 * @param {Array<String>} [params.refusals] the booking preview's refusals
	 * @return {String|null}
	 */
	describeBookingBlock({ proposal, refusals = [] }) {
		const blockedByFacts = proposal && Array.isArray(proposal.blockedByFacts) ? proposal.blockedByFacts : [];
		const reviewRefusals = blockedByFacts.map(fact => fact.refusal);
		const previewRefusals = refusals.filter(refusal => String(refusal).includes(UNPROVEN_FACT_REFUSAL_WORDS));
		const factRefusals = reviewRefusals.length > 0 ? reviewRefusals : previewRefusals;
		const uniqueRefusals = [...new Set(factRefusals)];
		if (uniqueRefusals.length === 0) return null;
		if (uniqueRefusals.length === 1) return uniqueRefusals[0];
		const countedSentence = `${uniqueRefusals.length} figures this entry answers are not in what QuickBooks returned, so it can't be booked. Re-run the playbook for fresh figures.`;
		return countedSentence;
	},

	/**
	 * Accepts or rejects one suggestion. A rejection carries a reason category and a sentence.
	 *
	 * @param {Object} params { runId, findingId | proposalId, decision, reasonCategory, reason }
	 * @return {Promise<Object>} the review
	 */
	decide({ runId, findingId, proposalId, decision, reasonCategory, reason, chosenValue }) {
		return postToAgents({ operationId: "decidePlaybookSuggestion", body: { runId, findingId, proposalId, decision, reasonCategory, reason, chosenValue } });
	},

	/**
	 * Carries last month's rejection of an unchanged suggestion forward as the viewer's decision.
	 *
	 * @param {Object} params { runId, findingId }
	 * @return {Promise<Object>} the review
	 */
	confirmStillApplies({ runId, findingId }) {
		return postToAgents({ operationId: "confirmPlaybookRejectionStillApplies", body: { runId, findingId } });
	},

	/**
	 * Accepts every undecided low finding on a run.
	 *
	 * @param {Object} params { runId }
	 * @return {Promise<Object>} the review
	 */
	acceptAllLow({ runId }) {
		return postToAgents({ operationId: "acceptAllLowPlaybookFindings", body: { runId } });
	},

	/**
	 * Re-checks the books now and returns the review with the fresh "after" picture.
	 *
	 * @param {Object} params { runId }
	 * @return {Promise<Object>} the review
	 */
	recheckBooks({ runId }) {
		return postToAgents({ operationId: "recheckPlaybookRunBooks", body: { runId } });
	},

	/**
	 * Sends a fully decided run to its reviewer.
	 *
	 * @param {Object} params { runId, note }
	 * @return {Promise<Object>} the review
	 */
	sendForReview({ runId, note }) {
		return postToAgents({ operationId: "sendPlaybookRunForReview", body: { runId, note } });
	},

	/**
	 * Signs a run off.
	 *
	 * @param {Object} params { runId }
	 * @return {Promise<Object>} the review
	 */
	signOff({ runId }) {
		return postToAgents({ operationId: "signOffPlaybookRun", body: { runId } });
	},

	/**
	 * Returns a run to the people who decide, with the reviewer's reason.
	 *
	 * @param {Object} params { runId, reason }
	 * @return {Promise<Object>} the review
	 */
	returnToPreparer({ runId, reason }) {
		return postToAgents({ operationId: "returnPlaybookRunToPreparer", body: { runId, reason } });
	},

	/**
	 * Carries out one proposed Workflow action — for example creating a task and emailing its
	 * assignee. Resolves to the review, freshly read, with the action executed or failed.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @param {Number} params.proposalId
	 * @return {Promise<Object>} the review
	 */
	executeAction({ runId, proposalId }) {
		return postToAgents({ operationId: "executePlaybookAction", body: { runId, proposalId } });
	},

	/**
	 * What booking one accepted journal proposal in QuickBooks would send, read against QuickBooks
	 * now: the lines, each account's balance when proposed and now, every reason it cannot be booked,
	 * and the warnings a person must acknowledge.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @param {Number} params.proposalId
	 * @return {Promise<Object>} { txnDate, memo, totalCents, lines, canBook, refusals, warnings }
	 */
	previewJournalBooking({ runId, proposalId }) {
		return postToAgents({ operationId: "previewPlaybookJournalBooking", body: { runId, proposalId } });
	},

	/**
	 * Books one accepted journal proposal in the client's QuickBooks. Resolves to the review, freshly
	 * read; a refusal or a failed booking rejects with the reason.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @param {Number} params.proposalId
	 * @param {Array<String>} params.acknowledgedWarnings
	 * @return {Promise<Object>} the review
	 */
	bookJournal({ runId, proposalId, acknowledgedWarnings }) {
		return postToAgents({ operationId: "bookPlaybookJournal", body: { runId, proposalId, acknowledgedWarnings } });
	},

	/**
	 * Removes a journal proposal's booked journal from the client's QuickBooks. Resolves to the
	 * review; a refusal rejects, and one for a journal changed in QuickBooks since it was booked
	 * carries journalChangedInQuickBooks on the error's response, after which removeEvenIfChanged
	 * deletes it anyway.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @param {Number} params.proposalId
	 * @param {Boolean} [params.removeEvenIfChanged]
	 * @return {Promise<Object>} the review
	 */
	removeBookedJournal({ runId, proposalId, removeEvenIfChanged }) {
		return postToAgents({ operationId: "removePlaybookJournal", body: { runId, proposalId, removeEvenIfChanged: removeEvenIfChanged === true } });
	},

	/**
	 * Where a journal proposal stands for booking in QuickBooks, and what this person may press on it.
	 * The state comes from the proposal alone (its status and its live decision); what may be pressed
	 * comes only from the review's viewer, which the api works out with the same rule it books by, so
	 * the screen holds no rule of its own.
	 *
	 * An entry nobody has decided is ready to accept and book in one go: booking records the booker's
	 * accept. An accepted one is ready to book. A rejected one offers nothing. One that failed always
	 * shows its failure, and can be tried again, still accepting it when nobody had decided it. A
	 * booked one names its QuickBooks journal, and says so when QuickBooks' copy did not match what was
	 * sent; someone who may book may also remove it (isRemovable). One removed after booking says who
	 * removed it, or that a re-check of the books found it deleted in QuickBooks, and offers nothing:
	 * removal is final. One whose booking or removal started and never finished is interrupted: nothing
	 * is offered, because QuickBooks may or may not hold it, so a person checks first.
	 *
	 * When the entry is still to book and this person may not book, whyNotText carries the api's own
	 * reason (viewer.whyNotBookJournals). isAcceptedByBooking says the booking would also accept it.
	 * isDecisionChangeable says the decision on it may still change: never once it is booked, removed
	 * or interrupted, because the api refuses a changed decision on a journal QuickBooks may hold.
	 *
	 * @param {Object} params
	 * @param {Object} params.proposal
	 * @param {Object} params.viewer the review's viewer: canBookJournals, whyNotBookJournals
	 * @return {Object} { state: ready_to_accept_and_book | ready | rejected | booked | removed | failed | interrupted, tone, isBookable, isRemovable, isAcceptedByBooking, isDecisionChangeable, buttonLabel, whyNotText, outcomeText }
	 */
	describeJournalBooking({ proposal, viewer }) {
		const canBookJournals = viewer.canBookJournals === true;
		const nothingToPress = { isBookable: false, isRemovable: false, isAcceptedByBooking: false, isDecisionChangeable: false, buttonLabel: "", whyNotText: "" };
		if (proposal.executionState === "interrupted") {
			const wasRemoving = proposal.status === "executed";
			const outcomeText = wasRemoving
				? "Removing it from QuickBooks was started and did not finish. Check the client's QuickBooks for this journal before doing anything else."
				: "Booking it was started and did not finish. Check the client's QuickBooks for this journal before doing anything else.";
			return { ...nothingToPress, state: "interrupted", tone: "warning", outcomeText };
		}
		if (proposal.status === "removed") {
			const result = proposal.executionResult || {};
			const when = moment(proposal.removedAt).format("MMM D, h:mm a");
			const wasDeletedInQuickBooks = proposal.removed_where === "quickbooks";
			const outcomeText = wasDeletedInQuickBooks
				? `Booked as QuickBooks journal ${result.qboJournalEntryId}, then found deleted in QuickBooks when the books were re-checked · ${when}.`
				: `Booked as QuickBooks journal ${result.qboJournalEntryId}, then removed from QuickBooks by ${proposal.removedBy} · ${when}.`;
			return { ...nothingToPress, state: "removed", tone: "neutral", outcomeText };
		}
		if (proposal.status === "executed") {
			const result = proposal.executionResult || {};
			const readBack = result.readBack || { matches: false, readFailed: "no read-back was kept" };
			const isReadBackMissing = !!readBack.readFailed;
			const isReadBackDifferent = !readBack.readFailed && !readBack.matches;
			let readBackText = "";
			if (isReadBackMissing) readBackText = " QuickBooks' copy could not be read back to compare — open the journal in QuickBooks.";
			if (isReadBackDifferent) readBackText = " QuickBooks' copy does not match what was sent — open the journal in QuickBooks.";
			const outcomeText = `Booked in QuickBooks as journal ${result.qboJournalEntryId} by ${proposal.executedBy} · ${moment(proposal.executedAt).format("MMM D, h:mm a")}.${readBackText}`;
			const bookedTone = readBack.matches ? "success" : "warning";
			return { ...nothingToPress, state: "booked", tone: bookedTone, isRemovable: canBookJournals, outcomeText };
		}
		const isRejected = !!proposal.decision && proposal.decision.decision === "reject";
		if (isRejected) return { ...nothingToPress, state: "rejected", tone: "neutral", isDecisionChangeable: true, outcomeText: "" };
		const isAccepted = !!proposal.decision && proposal.decision.decision === "accept";
		const isAcceptedByBooking = !isAccepted && canBookJournals;
		const whyNotBookJournals = viewer.whyNotBookJournals || "";
		const whyNotText = canBookJournals ? "" : whyNotBookJournals;
		const stillToBook = { isBookable: canBookJournals, isRemovable: false, isAcceptedByBooking, isDecisionChangeable: true, whyNotText };
		if (proposal.status === "failed") {
			const outcomeText = `It was not booked${this.describeLastAttempt({ proposal })}: ${proposal.executionError || "the server gave no reason"}`;
			const buttonLabel = isAccepted ? "Try booking again" : "Try accepting and booking again";
			return { ...stillToBook, state: "failed", tone: "danger", buttonLabel, outcomeText };
		}
		if (isAccepted) return { ...stillToBook, state: "ready", tone: "neutral", buttonLabel: "Book in QuickBooks", outcomeText: "" };
		return { ...stillToBook, state: "ready_to_accept_and_book", tone: "neutral", buttonLabel: "Accept and book in QuickBooks", outcomeText: "" };
	},

	/**
	 * Who last tried a failed proposal and when, as " (tried by Name · Oct 21, 3:05 pm)", or empty
	 * when nobody is recorded. A Workflow action and a journal booking say it the same way.
	 *
	 * @param {Object} params
	 * @param {Object} params.proposal
	 * @return {String}
	 */
	describeLastAttempt({ proposal }) {
		if (!proposal.lastFailedBy) return "";
		const triedText = ` (tried by ${proposal.lastFailedBy} · ${moment(proposal.lastFailedAt).format("MMM D, h:mm a")})`;
		return triedText;
	},

	/**
	 * A booking preview laid out for the shared QuickBooks journal modal: one line per account with
	 * its debit or credit in dollars, and the totals.
	 *
	 * @param {Object} params
	 * @param {Object} params.preview from previewJournalBooking
	 * @return {Object} { lines: [{ key, isLinked, quickBooksName, chartName, candidates, debit, credit }], totals: { debits, credits } }
	 */
	describeJournalBookingForConfirmation({ preview }) {
		const lines = preview.lines.map(line => ({
			key: line.lineNumber,
			isLinked: true,
			quickBooksName: line.accountName,
			chartName: line.accountName,
			candidates: [],
			debit: line.side === "debit" ? line.amountCents / 100 : 0,
			credit: line.side === "credit" ? line.amountCents / 100 : 0,
		}));
		const centsOnSide = side => preview.lines.filter(line => line.side === side).reduce((sum, line) => sum + line.amountCents, 0);
		const totals = { debits: centsOnSide("debit") / 100, credits: centsOnSide("credit") / 100 };
		return { lines, totals };
	},

	/**
	 * A run's proposals split into journal entries and Workflow actions, by the kind the server
	 * gives every proposal, on the run and in its review alike.
	 *
	 * @param {Object} params
	 * @param {Array} params.proposals
	 * @return {Object} { journals, workflowActions }
	 */
	splitProposalsByKind({ proposals }) {
		const allProposals = proposals || [];
		const workflowActions = allProposals.filter(proposal => proposal.kind === PROPOSAL_KIND.workflow);
		const journals = allProposals.filter(proposal => proposal.kind === PROPOSAL_KIND.journal);
		const split = { journals, workflowActions };
		return split;
	},

	/**
	 * The title of the report section that holds proposed Workflow actions — also its key for
	 * opening and closing it.
	 *
	 * @return {String}
	 */
	workflowActionsSectionTitle() {
		return WORKFLOW_ACTIONS_SECTION_TITLE;
	},

	/**
	 * Why the person looking at a run cannot act on its proposed Workflow actions. A test run
	 * only shows what the agent would propose, so nobody acts from it. A run with a review gives the
	 * api's own reason: why this person may not execute (viewer.whyNotExecuteActions) when they may
	 * not, otherwise who decides (viewer.whyNotDecide), for someone who may execute but not reject.
	 * A run without one is either a run covering several
	 * clients, whose actions are decided on each client's own run, or a run still working,
	 * whose actions can be decided once it finishes.
	 *
	 * @param {Object} params
	 * @param {Object} params.run
	 * @param {Object|null} params.review
	 * @return {String}
	 */
	whyWorkflowActionsWait({ run, review }) {
		const isTestRun = run.run_trigger === "test";
		if (isTestRun) return "A test run only shows what the playbook would do. Publish it and run it on the client to act on it.";
		if (review) {
			const { canExecuteActions, whyNotExecuteActions, whyNotDecide } = review.viewer;
			const whyTheyWait = canExecuteActions ? whyNotDecide : whyNotExecuteActions;
			return whyTheyWait;
		}
		const isSeveralClientsRun = run.client_id === null;
		if (isSeveralClientsRun) return "Each client's actions are decided on that client's own run, listed below.";
		return "The run is still working. Its actions can be decided once it finishes.";
	},

	/**
	 * Whether a proposed Workflow action needs nothing more: it was carried out, a person
	 * accepted it to make by hand, or a person rejected it.
	 *
	 * @param {Object} params
	 * @param {Object} params.proposal
	 * @return {Boolean}
	 */
	isWorkflowActionSettled({ proposal }) {
		const isExecuted = proposal.status === "executed";
		const isDecided = !!proposal.decision;
		const isSettled = isExecuted || isDecided;
		return isSettled;
	},

	/**
	 * Where a proposed Workflow action stands, for its review card: the card's look, whether it
	 * can still be carried out or rejected, and the line that says what happened.
	 *
	 * An action is accepted only by executing it, so it is open, executed, failed or rejected. An
	 * action someone started executing that never finished is interrupted: it is never run again,
	 * so it offers nothing to press and asks the person to check Workflow for the change.
	 *
	 * @param {Object} params
	 * @param {Object} params.proposal
	 * @return {Object} { state, look, isActionable, outcomeText, resultText }
	 */
	describeWorkflowAction({ proposal }) {
		if (proposal.executionState === "interrupted") {
			const outcomeText = "Executing it was started and did not finish. Check Workflow to see whether the change was made.";
			return { state: "interrupted", look: "warn", isActionable: false, outcomeText, resultText: "" };
		}
		const isRejected = !!proposal.decision && proposal.decision.decision === "reject";
		if (isRejected) {
			const { decidedBy, decidedAt } = proposal.decision;
			const outcomeText = `Rejected by ${decidedBy} · ${moment(decidedAt).format("MMM D")} — ${this.describeRejection({ decision: proposal.decision })}`;
			return { state: "rejected", look: "na", isActionable: false, outcomeText, resultText: "" };
		}
		if (proposal.status === "executed") {
			const outcomeText = `Done by ${proposal.executedBy} · ${moment(proposal.executedAt).format("MMM D, h:mm a")}`;
			const resultText = this.describeExecutionResult({ result: proposal.executionResult });
			return { state: "executed", look: "pass", isActionable: false, outcomeText, resultText };
		}
		if (proposal.status === "failed") {
			const outcomeText = `It did not go through${this.describeLastAttempt({ proposal })}: ${proposal.executionError || "the server gave no reason"}`;
			return { state: "failed", look: "fail", isActionable: true, outcomeText, resultText: "" };
		}
		return { state: "open", look: "info", isActionable: true, outcomeText: "", resultText: "" };
	},

	/**
	 * What carrying out an action did, in words: the summary inside its result record.
	 *
	 * @param {Object} params
	 * @param {Object|null} params.result
	 * @return {String}
	 */
	describeExecutionResult({ result }) {
		if (!result) return "";
		const summary = result.summary ? String(result.summary) : "";
		return summary;
	},

	/**
	 * The reasons a suggestion can be rejected for, as select choices.
	 *
	 * @return {Array} [{ value, label }]
	 */
	rejectReasonChoices() {
		const choices = Object.keys(REJECT_REASON_LABELS).map(category => ({ value: category, label: REJECT_REASON_LABELS[category] }));
		return choices;
	},

	/**
	 * Whether a decision form can be saved and, if not, what is still missing. Accepting with an
	 * answer needs only the answer. Rejecting needs a reason always; a note only when the reason
	 * is "other"; and, when the suggestion was a choice among a list, the right answer — unless
	 * the reason is that the information is missing, which is exactly when nobody knows it yet.
	 *
	 * @param {Object} params
	 * @param {Boolean} params.isRejection
	 * @param {String} params.reasonCategory
	 * @param {String} params.reason
	 * @param {Object|null} params.choices { kind, options } from choicesFor
	 * @param {String} params.chosenId
	 * @return {Object} { isReady, isNoteRequired, missing }
	 */
	describeDecisionReadiness({ isRejection, reasonCategory, reason, choices, chosenId }) {
		const thing = choices && choices.kind === "account" ? "account" : "answer";
		const isChoiceMissing = !!choices && !chosenId;
		if (!isRejection) {
			const acceptMissing = isChoiceMissing ? `Choose the right ${thing}.` : null;
			return { isReady: !acceptMissing, isNoteRequired: false, missing: acceptMissing };
		}
		const isNoteRequired = reasonCategory === REASON_NEEDING_A_NOTE;
		let missing = null;
		if (isChoiceMissing && reasonCategory !== REASON_WITHOUT_AN_ANSWER)
			missing = `Choose the right ${thing}, or the reason that the information is missing.`;
		else if (!reasonCategory) missing = "Choose a reason.";
		else if (isNoteRequired && !String(reason || "").trim()) missing = "Say why in a sentence.";
		return { isReady: !missing, isNoteRequired, missing };
	},

	/**
	 * Whether accepting a finding needs the person to name the answer: the finding is a choice
	 * among a list and the agent's answer is not one of the listed choices (no account in the
	 * chart, say), so agreeing with it is only complete with the right answer.
	 *
	 * @param {Object} params
	 * @param {Object} params.subject a finding from the review
	 * @param {Object|null} params.choices { kind, options } from choicesFor
	 * @return {Boolean}
	 */
	needsAnswerToAccept({ subject, choices }) {
		const record = subject.decisionRecord;
		if (!choices || !record) return false;
		const modelChoiceId = record.modelValue ? record.modelValue.choiceId : null;
		const isListed = choices.options.some(option => option.value === modelChoiceId);
		return !isListed;
	},

	/**
	 * What a person can pick from when a suggestion was a choice among a list: the chart of
	 * accounts the agent chose from, or a declared list such as tax treatments, as select
	 * options with the kind of list; null when there is nothing to choose instead.
	 *
	 * @param {Object} params
	 * @param {Object} params.subject a finding from the review
	 * @param {Object} params.review
	 * @return {Object|null} { kind: "account" | "list", options: [{ value, label, hint }] }
	 */
	choicesFor({ subject, review }) {
		const record = subject.decisionRecord;
		if (!record || !record.choicesId) return null;
		const entries = review.choicesById[record.choicesId];
		const options = entries.map(entry => ({ value: entry.choiceId, label: entry.label, hint: entry.hint || undefined }));
		return { kind: record.choiceKind, options };
	},

	/**
	 * The words for a reject reason category.
	 *
	 * @param {Object} params
	 * @param {String} params.category
	 * @return {String}
	 */
	describeRejectReason({ category }) {
		const label = UtilityService.labelFor({ labels: REJECT_REASON_LABELS, key: category });
		return label;
	},

	/**
	 * The label and tone for where a run is in decisions and sign-off, or null when it needs none.
	 *
	 * @param {Object} params
	 * @param {String} params.stage
	 * @return {Object|null} { label, tone }
	 */
	describeStage({ stage }) {
		return STAGE_DESCRIPTIONS[stage] || null;
	},

	/**
	 * Whether a run is in the decide and sign-off flow: it has a review, and the review's stage is
	 * one the flow shows (a run that needs no decisions has none).
	 *
	 * @param {Object} params
	 * @param {Object|null} params.review
	 * @return {Boolean}
	 */
	isInDecisionFlow({ review }) {
		const isInFlow = !!review && !!this.describeStage({ stage: review.stage });
		return isInFlow;
	},

	/**
	 * Plain words for where a run's sign-off record entry stands.
	 *
	 * @param {Object} params
	 * @param {String} params.stage sent_for_review | signed_off | returned
	 * @return {String}
	 */
	describeSignoffStage({ stage }) {
		const label = UtilityService.labelFor({ labels: SIGNOFF_STAGE_LABELS, key: stage });
		return label;
	},

	/**
	 * Last month's decision on the same problem, in plain words: who decided what, why when it was
	 * a rejection, and whether the amount is the same now.
	 *
	 * @param {Object} params
	 * @param {Object} params.lastMonth { decision, isSameAmount, amount }
	 * @return {Object} { isRejection, decisionLabel, decidedBy, reasonText, amountNote }
	 */
	describeLastMonthDecision({ lastMonth }) {
		const previous = lastMonth.decision;
		const isRejection = previous.decision === "reject";
		const decisionLabel = isRejection ? "Rejected" : "Accepted";
		const reasonText = isRejection ? this.describeRejection({ decision: previous }) : null;
		const lastMonthAmount = Math.abs(Number(lastMonth.amount) || 0);
		const amountNote = lastMonth.isSameAmount ? "same amount" : `then $${UtilityService.formatCurrency(lastMonthAmount)}`;
		const description = { isRejection, decisionLabel, decidedBy: previous.decidedBy, reasonText, amountNote };
		return description;
	},

	/**
	 * What the stage bar above a run's review says: progress or who it waits on, whether nobody holds
	 * the role it waits on, the latest return to the preparer, how many low findings are still
	 * undecided, and the note under it for someone who cannot decide everything here. That note is
	 * only for a run waiting on decisions: a decider gets none; someone who may decide journal entries
	 * but not findings gets none when no finding needs a decision (they can decide everything there
	 * is), and otherwise, on a run with journal entries still to decide (describeJournalBooking's
	 * isDecisionChangeable: not booked, removed or interrupted), is told who decides the findings and
	 * that they can decide the journal entries below; anyone else, and a journal decider with no journal
	 * entry left to decide, reads the api's reason they cannot decide, as before.
	 *
	 * @param {Object} params
	 * @param {Object} params.review
	 * @return {Object} { lead, isUnassigned, latestReturn, undecidedLowCount, decideNote }
	 */
	describeStageBar({ review }) {
		const { stage, openCount, deciders, signers, signoffs, viewer } = review;
		const decidableFindingCount = review.findings.filter(finding => finding.needsDecision).length;
		const decidableCount = decidableFindingCount + review.proposals.length;
		const decidedCount = decidableCount - openCount;
		const deciderNames = deciders.map(person => person.name).join(", ");
		const signerNames = signers.map(person => person.name).join(", ");
		const undecidedLowCount = review.findings.filter(finding => finding.severity === "low" && !finding.decision).length;
		const newestFirstSignoffs = [...signoffs].reverse();
		const latestSent = newestFirstSignoffs.find(signoff => signoff.stage === "sent_for_review" && !signoff.withdrawnAt) || null;
		const latestReturn = newestFirstSignoffs.find(signoff => signoff.stage === "returned") || null;
		const signedOff = signoffs.find(signoff => signoff.stage === "signed_off") || null;

		const isSignoff = stage === "waiting_on_signoff";
		const nobody = PlaybookWaitingService.describeUnassigned({ isSignoff, whyNobody: isSignoff ? review.whyNobodySignsOff : review.whyNobodyDecides });
		let lead = null;
		if (stage === "needs_decisions") lead = deciderNames ? `${decidedCount} of ${decidableCount} decided · decided by ${deciderNames}` : `${nobody.title}. ${nobody.detail || ""}`.trim();
		if (isSignoff) {
			const sentNote = latestSent ? ` · sent by ${latestSent.by} ${moment(latestSent.at).format("MMM D")}` : "";
			lead = signerNames ? `Waiting on sign-off by ${signerNames}${sentNote}` : `${nobody.title}. ${nobody.detail || ""}`.trim();
		}
		if (stage === "signed_off" && signedOff) {
			const selfReviewNote = signedOff.selfReviewed ? " · self-reviewed (allowed by the playbook's settings)" : "";
			lead = `Signed off by ${signedOff.by} on ${moment(signedOff.at).format("MMM D")}${selfReviewNote}`;
		}
		if (stage === "complete") lead = "Every suggestion is decided. This playbook does not need a sign-off.";
		const isUnassigned = (stage === "needs_decisions" && !deciderNames) || (stage === "waiting_on_signoff" && !signerNames);
		const isWaitingOnDecisions = stage === "needs_decisions";
		const hasJournalsToDecide = review.proposals.some(proposal => proposal.kind === "journal" && this.describeJournalBooking({ proposal, viewer }).isDecisionChangeable);
		const hasFindingsLeftToOthers = decidableFindingCount > 0;
		const decidesOnlyJournals = !viewer.canDecide && viewer.canDecideJournals;
		const whoDecidesFindings = deciderNames ? `Findings here are decided by ${deciderNames}.` : viewer.whyNotDecide;
		const notDecidingReason = viewer.whyNotDecide || null;
		const journalDeciderNote = hasJournalsToDecide ? `${whoDecidesFindings} You can decide the journal entries below.` : notDecidingReason;
		let decideNote = null;
		if (isWaitingOnDecisions && !viewer.canDecide) decideNote = notDecidingReason;
		if (isWaitingOnDecisions && decidesOnlyJournals) decideNote = hasFindingsLeftToOthers ? journalDeciderNote : null;
		const stageBar = { lead, isUnassigned, latestReturn, undecidedLowCount, decideNote };
		return stageBar;
	},

	/**
	 * Findings grouped high, medium, low, leaving out empty groups.
	 *
	 * @param {Object} params
	 * @param {Array} params.findings
	 * @return {Array} [{ severity, label, findings }]
	 */
	groupFindingsBySeverity({ findings }) {
		const allFindings = findings || [];
		const groups = SEVERITY_GROUPS.map(group => ({ ...group, findings: allFindings.filter(finding => finding.severity === group.severity) }));
		const nonEmptyGroups = groups.filter(group => group.findings.length > 0);
		return nonEmptyGroups;
	},

	/**
	 * The title of the report section that holds proposed journal entries — also its key for
	 * opening and closing it.
	 *
	 * @return {String}
	 */
	proposalsSectionTitle() {
		return PROPOSALS_SECTION_TITLE;
	},

	/**
	 * Which report sections start closed: those where everything was already decided when the
	 * report opened, and for journal entries every one booked, removed or rejected, so an entry still
	 * waiting to be booked (or interrupted while booking) keeps its section open. Whether a journal is
	 * finished is read from its state alone, never from what this person may press, so the section
	 * opens the same for everyone. Worked out once, so deciding the last suggestion in a section
	 * never closes it under the person's hand.
	 *
	 * @param {Object} params
	 * @param {Object} params.review
	 * @return {Object} { [section title]: true }
	 */
	initiallyCollapsedSections({ review }) {
		const collapsed = {};
		this.groupFindingsByArea({ findings: review.findings }).forEach(section => {
			if (section.decidedCount === section.findings.length) collapsed[section.area] = true;
		});
		const { journals, workflowActions } = this.splitProposalsByKind({ proposals: review.proposals });
		const isJournalFinished = proposal => {
			const booking = this.describeJournalBooking({ proposal, viewer: review.viewer });
			const isFinished = FINISHED_JOURNAL_STATES.includes(booking.state);
			return isFinished;
		};
		const areAllJournalsFinished = journals.length > 0 && journals.every(isJournalFinished);
		if (areAllJournalsFinished) collapsed[PROPOSALS_SECTION_TITLE] = true;
		const areAllActionsSettled = workflowActions.length > 0 && workflowActions.every(proposal => this.isWorkflowActionSettled({ proposal }));
		if (areAllActionsSettled) collapsed[WORKFLOW_ACTIONS_SECTION_TITLE] = true;
		return collapsed;
	},

	/**
	 * Findings in sections by what was checked, the most serious section first and, inside each,
	 * high before medium before low. Each section counts how many are decided.
	 *
	 * @param {Object} params
	 * @param {Array} params.findings
	 * @return {Array} [{ area, findings, decidedCount, highestSeverity }]
	 */
	groupFindingsByArea({ findings }) {
		const severityRank = { high: 0, medium: 1, low: 2 };
		const sections = [];
		(findings || []).forEach(finding => {
			let section = sections.find(candidate => candidate.area === finding.area);
			if (!section) {
				section = { area: finding.area, findings: [] };
				sections.push(section);
			}
			section.findings.push(finding);
		});
		sections.forEach(section => {
			section.findings.sort((first, second) => severityRank[first.severity] - severityRank[second.severity]);
			section.decidedCount = section.findings.filter(finding => !!finding.decision).length;
			section.highestSeverity = section.findings[0].severity;
		});
		sections.sort((first, second) => severityRank[first.highestSeverity] - severityRank[second.highestSeverity] || first.area.localeCompare(second.area));
		return sections;
	},

	/**
	 * How a finding's card looks: by severity while it is open, settled once a person rejected it
	 * or the books show it fixed.
	 *
	 * @param {Object} params
	 * @param {Object} params.finding
	 * @return {String} fail | warn | info | pass | na
	 */
	reviewLookFor({ finding }) {
		const decision = finding.decision ? finding.decision.decision : null;
		if (decision === "reject") return "na";
		if (decision === "accept" && finding.after && finding.after.state === "fixed") return "pass";
		const lookBySeverity = { high: "fail", medium: "warn", low: "info" };
		return lookBySeverity[finding.severity];
	},

	/**
	 * The tag on a finding's resolution line: what kind of fix it is.
	 *
	 * @param {Object} params
	 * @param {Object} params.finding
	 * @return {String}
	 */
	resolutionKindFor({ finding }) {
		if (finding.checkId === GL_ALLOCATION_CHECK_ID) return "Recode";
		if (finding.source === "judgement") return "Agent's judgement";
		return "Correction";
	},

	/**
	 * Which approved script worked a finding out, in words, read only from the scriptSource the
	 * review gives the finding: the script, its revision, whether its approval came from the firm's
	 * scripts, who approved it and when. The server sends scriptSource only when the finding cites a
	 * row that script returned, so a finding without one has no line. An approver the firm no longer
	 * lists is named as such rather than left blank.
	 *
	 * @param {Object} params
	 * @param {Object|null} params.scriptSource { scriptKey, revisionNumber, approvalSource, approvedBy, approvedAt }
	 * @return {String|null}
	 */
	describeScriptSource({ scriptSource }) {
		if (!scriptSource) return null;
		const { scriptKey, revisionNumber, approvalSource, approvedBy, approvedAt } = scriptSource;
		const isFromLibrary = approvalSource === SCRIPT_FROM_LIBRARY;
		const libraryText = isFromLibrary ? " from the firm's scripts" : "";
		const approverName = approvedBy || APPROVER_NO_LONGER_LISTED;
		const approvedOn = moment(approvedAt).format("MMM D, YYYY");
		const sentence = `Worked out by the script ${scriptKey} (revision ${revisionNumber})${libraryText}, approved by ${approverName} on ${approvedOn}.`;
		return sentence;
	},

	/**
	 * The three steps that close a finding — decide, fix in QuickBooks, sign-off — each with its
	 * state, for the review card. A rejected finding needs no fix; a run that needs no sign-off
	 * has no sign-off step; a journal proposal's middle step is booking it in QuickBooks, done once
	 * the proposal is booked. An undecided journal whose decision cannot change (its booking started
	 * and never finished) has an on-hold decide step instead, because the api refuses any decision on
	 * it until someone checks QuickBooks. A journal whose booking started and never finished has an
	 * on-hold booking step too, because QuickBooks may already hold it and nothing may be pressed until
	 * someone checks.
	 *
	 * @param {Object} params
	 * @param {Object} params.subject a finding or a journal proposal
	 * @param {Boolean} params.isProposal
	 * @param {Object} params.review the run's review: stage, signoffRequired, signers, signoffs
	 * @return {Array} [{ id, label, status, detail }] status: completed | waiting_for_human | blocked | not_started | not_applicable
	 */
	describeFindingSteps({ subject, isProposal, review }) {
		const { decision, after } = subject;
		const isRejected = decision && decision.decision === "reject";
		const journalBooking = isProposal && subject.kind === PROPOSAL_KIND.journal ? this.describeJournalBooking({ proposal: subject, viewer: review.viewer || {} }) : null;
		const isDecisionOnHold = !!journalBooking && !decision && !journalBooking.isDecisionChangeable;
		const isBookingOnHold = !!journalBooking && journalBooking.state === "interrupted";
		const steps = [];

		if (decision) {
			const decisionText = isRejected
				? `Rejected by ${decision.decidedBy} — ${this.describeRejection({ decision, choiceKind: subject.decisionRecord ? subject.decisionRecord.choiceKind : null })}`
				: `Accepted by ${decision.decidedBy}`;
			steps.push({
				id: "decide",
				label: isRejected ? "Rejected" : "Accepted",
				status: "completed",
				detail: `${decisionText} · ${moment(decision.decidedAt).format("MMM D")}${decision.carriedFromDecisionId ? " · carried from last month" : ""}`,
			});
		} else if (isDecisionOnHold) {
			steps.push({ id: "decide", label: "On hold until this journal is checked in QuickBooks", status: "blocked", detail: null });
		} else {
			steps.push({
				id: "decide",
				label: subject.needsDecision === false ? "Accept or reject — optional for this one" : "Accept or reject this suggestion",
				status: "waiting_for_human",
				detail: null,
			});
		}

		if (isProposal && subject.status === "executed") {
			steps.push({ id: "fix", label: "Booked in QuickBooks", status: "completed", detail: null });
		} else if (isProposal && subject.status === "removed") {
			steps.push({ id: "fix", label: "Removed from QuickBooks", status: "blocked", detail: "It was booked and then removed, so the fix is not in the books. Run the playbook again for a fresh entry." });
		} else if (isBookingOnHold) {
			steps.push({ id: "fix", label: "On hold — check this journal in QuickBooks", status: "blocked", detail: "Booking it was started and did not finish, so QuickBooks may already hold it." });
		} else if (isProposal) {
			steps.push({
				id: "fix",
				label: "Book the entry in QuickBooks",
				status: isRejected ? "not_applicable" : decision ? "waiting_for_human" : "not_started",
				detail: isRejected ? null : "Nothing is posted until someone books it below, after QuickBooks is read again, or enters it in QuickBooks by hand.",
			});
		} else if (isRejected) {
			steps.push({ id: "fix", label: "Fix in QuickBooks", status: "not_applicable", detail: null });
		} else if (!decision) {
			steps.push({ id: "fix", label: "Fix in QuickBooks", status: "not_started", detail: null });
		} else if (after && after.state === "fixed") {
			steps.push({ id: "fix", label: "Fixed in QuickBooks", status: "completed", detail: "The re-check of the books found it cleared." });
		} else if (after && after.state === "not_rechecked") {
			steps.push({
				id: "fix",
				label: "Fix in QuickBooks",
				status: "completed",
				detail: "Code cannot re-check this kind of finding; the reviewer confirms it at sign-off.",
			});
		} else if (after && after.state === "still_there") {
			const amountNow = after.amountNow !== null ? ` · $${UtilityService.formatCurrency(Math.abs(Number(after.amountNow)))}` : "";
			steps.push({
				id: "fix",
				label: "Still in the books",
				status: "blocked",
				detail: `The re-check still found it${amountNow}. Fix it in QuickBooks, then re-check the books.`,
			});
		} else if (after && after.state === "not_checked") {
			steps.push({
				id: "fix",
				label: "Fix in QuickBooks",
				status: "waiting_for_human",
				detail: "The last re-check could not read this from QuickBooks. Confirm the fix there, then re-check the books.",
			});
		} else if (after && after.state === "changed") {
			steps.push({
				id: "fix",
				label: "Changed since the run",
				status: "waiting_for_human",
				detail: "The amount moved but did not clear. Look again in QuickBooks.",
			});
		} else if (after && after.state === "moved_elsewhere") {
			const whereNow = after.accountNow ? `to ${after.accountNow}` : "to an account";
			steps.push({
				id: "fix",
				label: "Moved to a different account",
				status: "waiting_for_human",
				detail: `The re-check found the line moved ${whereNow}, not the one suggested. Confirm that account is right in QuickBooks, then re-check the books.`,
			});
		} else if (after && after.state === "gone") {
			steps.push({
				id: "fix",
				label: "No longer in this period",
				status: "waiting_for_human",
				detail:
					"The re-check could not find the line in this period. It may have been deleted, re-dated or merged — confirm what happened to it in QuickBooks.",
			});
		} else {
			steps.push({ id: "fix", label: "Fix in QuickBooks", status: "waiting_for_human", detail: "Sending for review re-checks the books." });
		}

		if (!review.signoffRequired) {
			steps.push({ id: "signoff", label: "Sign-off", status: "not_applicable", detail: null });
		} else if (review.stage === "signed_off") {
			const signedOff = review.signoffs.find(signoff => signoff.stage === "signed_off");
			steps.push({
				id: "signoff",
				label: "Signed off",
				status: "completed",
				detail: signedOff ? `By ${signedOff.by} · ${moment(signedOff.at).format("MMM D")}` : null,
			});
		} else if (review.stage === "waiting_on_signoff") {
			const signerNames = review.signers.map(person => person.name).join(", ");
			steps.push({
				id: "signoff",
				label: "Waiting on sign-off",
				status: "waiting_for_human",
				detail: signerNames ? `By ${signerNames}` : "Nobody on this client's team holds the sign-off role.",
			});
		} else {
			steps.push({
				id: "signoff",
				label: "Sign-off by a reviewer",
				status: "not_started",
				detail: "After every suggestion is decided, send it for review.",
			});
		}

		return steps;
	},

	/**
	 * How many judgments a run recorded, in words for the review screen.
	 *
	 * @param {Object} params
	 * @param {Number} params.count
	 * @return {String}
	 */
	describeDecisionRecordCount({ count }) {
		const noun = count === 1 ? "judgment" : "judgments";
		return `${count} ${noun} recorded — each decision here becomes the answer for one of them`;
	},

	/**
	 * A rejection in words: the reason, the account chosen instead when there was one, and the
	 * person's note when they wrote one.
	 *
	 * @param {Object} params
	 * @param {Object} params.decision { reasonCategory, reason, chosenValue }
	 * @param {String|null} [params.choiceKind] "account" or "list" when the decision was a choice among a list
	 * @return {String}
	 */
	describeRejection({ decision, choiceKind = null }) {
		const reasonLabel = this.describeRejectReason({ category: decision.reasonCategory });
		const chosenLabel = decision.chosenValue ? decision.chosenValue.label : null;
		const verb = choiceKind === "account" ? "belongs in" : "should be";
		const chosenText = chosenLabel ? ` — ${verb} ${chosenLabel}` : "";
		const noteText = decision.reason ? `: ${decision.reason}` : "";
		return `${reasonLabel}${chosenText}${noteText}`;
	},

	/**
	 * The choices for deciding a suggestion: accept it, or say no and then why.
	 *
	 * @return {Array} [{ id, label }]
	 */
	decisionChoices() {
		return [
			{ id: "accept", label: "Yes, accept" },
			{ id: "reject", label: "No" },
		];
	},
};

export default PlaybookReviewService;
